import { datadogRum } from '@datadog/browser-rum';
import { ToastContainer } from '@montugroup/design-system';
import { CircularProgress, Stack, StyledEngineProvider, ThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import { Provider as GraphQLProvider, cacheExchange, createClient, dedupExchange, fetchExchange } from 'urql';
import './api/pmsAxios';
import AppRoutes from './components/AppRoutes';
import settings from './data/constants';
import { getUser } from './data/service/authService';
import { StoreProvider } from './data/store';
import useTrackingProviders from './hooks/useTrackingProviders';
import theme from './theme';
import { LaunchDarkly } from './utils/launchdarkly';

import '@montugroup/design-system/Toast/style.css';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import 'izitoast/dist/css/iziToast.min.css';
import ModalProvider from 'mui-modal-provider';
import './assets/scss/app.scss';
import { AppSideEffects } from './components/AppSideEffects';
import { FF_ENABLE_PMS_ERROR_HANDLING } from './constants/featureFlags';
import { useErrorHandler } from './error-handling';
import { useFeatureFlags } from './hooks';
import { TsrReactQueryProviders } from './providers/tsr-react-query-providers';
import { TourProvider } from './tours/TourProvider';
import { generateQueryConfig } from './utils/generateQueryConfig';

const THEME = createTheme(theme);

const client = createClient({
  url: settings.paymentGqlURL,
  exchanges: [dedupExchange, cacheExchange, fetchExchange],
});

const App = () => {
  const { handleErrorPage, handleErrorAlert } = useErrorHandler();
  const [loading, setLoading] = useState<boolean>(true);
  const user = getUser();
  const { flags } = useFeatureFlags();
  const ffEnableErrorHandling = flags[FF_ENABLE_PMS_ERROR_HANDLING];

  // Hook responsible for setting up all our tracking services. This can be expanded to include multiple services
  useTrackingProviders(settings);

  // Set up datadogRum user, so we can find users by userID in datadog
  // Safest and fastest implementation to solve for now
  if (
    user &&
    user.constructor === Object &&
    user.id !== undefined &&
    user.id !== null &&
    ['string', 'number'].includes(typeof user.id)
  ) {
    try {
      datadogRum.setUser({
        id: user.id,
      });
      console.info('The user has been in Datadog RUM:', user.id);
    } catch (error) {
      console.error('Failed to set user in Datadog RUM:', error);
    }
  }

  const DEFAULT_QUERY_CONFIG = generateQueryConfig(ffEnableErrorHandling, handleErrorPage, handleErrorAlert);

  // initialize new React Query instance
  const queryClient = new QueryClient(DEFAULT_QUERY_CONFIG);

  // initialise LD instance
  useEffect(() => {
    setLoading(true);
    LaunchDarkly.init(user).finally(() => {
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh" paddingLeft="300px">
        <CircularProgress size={30} />
      </Stack>
    );
  }

  return (
    <GoogleOAuthProvider clientId={settings.googleLoginClientID}>
      <GraphQLProvider value={client}>
        <StoreProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={THEME}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <LaunchDarkly.Provider>
                  <QueryClientProvider client={queryClient}>
                    <TsrReactQueryProviders>
                      <ReactQueryDevtools initialIsOpen={false} />
                      <ModalProvider>
                        <TourProvider>
                          <AppRoutes />
                          <ToastContainer />
                          <AppSideEffects />
                        </TourProvider>
                      </ModalProvider>
                    </TsrReactQueryProviders>
                  </QueryClientProvider>
                </LaunchDarkly.Provider>
              </LocalizationProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </StoreProvider>
      </GraphQLProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
